<template>
  <fieldset
    class="CheckButtonGroupComponent"
    :class="{ 'v-input--error': fieldValidationErrorMessage }"
  >
    <legend v-if="legend">{{ legend }}</legend>

    <div class="options">
      <label
        class="checkbox-card"
        v-for="option in options"
        :key="option.value"
      >
        <input
          type="checkbox"
          :id="`${id}-${option.value}`"
          v-model="value"
          :value="option.value"
          :hide-details="true"
          @update:model-value="emit('update:modelValue', value)"
          @keydown.enter.prevent="($event.target as HTMLInputElement).click()"
        />
        {{ option.label }}
      </label>
    </div>

    <v-text-field
      :key="innerValue"
      ref="$field"
      v-model="innerValue"
      style="display: none"
      :hide-details="true"
      :rules="validations"
    />

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </fieldset>
</template>

<script lang="ts" setup>
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import { ref, watch } from 'vue'
  import ErrorMessageComponent from '../formFields/ErrorMessageComponent.vue'

  import type { OptionsValuesInterface, ValidationRuleType } from '@/modules/core/models/types'

  interface PropsInterface {
    id: string
    legend?: string
    options: OptionsValuesInterface[]
    modelValue: string[] | null
    rules?: ValidationRuleType[]
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    options: () => [],
    rules: () => [],
    modelValue: () => []
  })

  const emit = defineEmits(['update:modelValue'])

  const value = ref(props.modelValue)
  const innerValue = ref(value.value?.length)

  const validations: ValidationRuleType[] = props.rules

  watch(
    () => value.value,
    () => (innerValue.value = value.value?.length)
  )

  const { $field, fieldValidationErrorMessage } = useValidationError()
</script>

<style lang="scss" scoped>
  .CheckButtonGroupComponent {
    border: none;
    margin-top: $spacing-l;

    legend {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-bottom: $spacing-l;

      &:empty {
        margin: 0;
      }
    }

    .options {
      display: grid;
      gap: $gap-m;

      .option {
        border: 1px solid $color-neutral-light;
        background: $color-neutral-lighter;
        padding: 0 $spacing-m;
      }

      .checkbox-card {
        background-color: $color-neutral-lighter;
        border: solid 1px $color-neutral-light;
        display: flex;
        align-items: center;
        gap: $gap-m;
        padding: $spacing-xl;
        cursor: pointer;
        font-size: $font-size-l;
        color: $color-neutral-dark;

        [type='checkbox'] {
          accent-color: $color-primary;
        }

        &:has(input:checked) {
          color: $color-primary;
          border-color: $color-primary;
          background-color: $color-primary-lighter;
        }
      }
    }
  }
</style>
